// Agreements: v6 for new drivers, Addendum No. 1 for drivers on v5, and the
// induction sessions both run through. db/migrations/3700 to 3707,
// docs/AGREEMENTS.md.
//
// Nobody on v5 is asked to sign anything until they have been at an Addendum
// No. 1 session: marking a driver present is the only thing that issues the
// SignNow envelope. A driver may decline, and a decline changes nothing else
// about their work (clause 9.4 of the addendum). Every refusal on this page is
// Postgres's sentence, unaltered.
import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CalendarPlus, FileSignature, FileText, Mail, RotateCcw, ShieldAlert, UserCheck, UserX, Users } from 'lucide-react'
import {
  useBookInductionSession, useCreateInductionSession, useDeclineAddendum, useGetAddendumInvitationPreview,
  useGetAgreements, useGetInductionBookable, useMarkInductionAttendance, useReopenAddendum,
  useResendAddendum, useSendAddendumInvitations,
} from '../hooks/backend/agreements'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { Chip } from '../components/interviews/Chip'
import { formatDay, formatWhen } from '../utils/loans'
import { useOpenAgreementFile } from '../hooks/useOpenAgreementFile'
import {
  ADDENDUM_STATUS, ATTENDANCE_LABEL, DOCUMENT_SET_LABEL, MESSAGE_STATUS_LABEL, SESSION_TYPE_LABEL, kb, sastInstant, shortHash,
  type AddendumRow, type AgreementsPage, type Bookable, type InductionSession, type InvitationPreview,
} from '../utils/agreements'

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function todaySast(): string {
  return new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)
}

export default function Agreements() {
  const pageQuery = useGetAgreements<AgreementsPage | null>()
  const load = pageQuery.trigger
  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { refresh() }, [refresh])
  const [newSession, setNewSession] = useState(false)
  const [inviteFor, setInviteFor] = useState<InductionSession | null>(null)

  const page = pageQuery.data
  if (pageQuery.loading && !page && !pageQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (pageQuery.error && !page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{pageQuery.error}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Agreements are open to staff logins.</p>
          <p className="text-xs text-muted-foreground">Ask an admin to switch on View for you in cockpit access.</p>
        </div>
      </div>
    )
  }

  const c = page.counts
  const addendumSessions = page.sessions.filter((s) => s.session_type === 'addendum_1')

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <FileSignature className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Agreements</h1>
          <p className="text-xs text-muted-foreground">
            {page.current
              ? `Every new contract is ${page.current.version}, in force from ${formatDay(page.current.in_force_from)}. Drivers on v5 are offered Addendum No. 1 at an induction session, and sign only after it.`
              : 'No agreement is in force.'}
          </p>
        </div>
        {page.can_record && (
          <Button size="sm" variant="outline" onClick={() => setNewSession(true)}><CalendarPlus className="mr-1 h-4 w-4" /> New session</Button>
        )}
      </div>
      {!page.can_record && (
        <p className="border-b border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          You can read this page. Sessions, attendance and declines are recorded by an admin or someone with Edit drivers in cockpit access.
        </p>
      )}
      {pageQuery.error && <p className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{pageQuery.error}</p>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <section className="grid grid-cols-2 gap-2 border-b border-border p-4 sm:grid-cols-4 lg:grid-cols-7">
          <Tile label="v6 issued" value={c.v6_issued} />
          <Tile label="v6 signed" value={c.v6_signed} />
          <Tile label="Addendum awaiting induction" value={c.addendum_awaiting_induction} />
          <Tile label="Addendum ready to sign" value={c.addendum_ready_to_sign} />
          <Tile label="Addendum signed" value={c.addendum_signed} />
          <Tile label="Addendum declined" value={c.addendum_declined} />
          <Tile label="On older agreements" value={c.older_agreements} />
        </section>

        <Sessions page={page} onChanged={refresh} onInvite={setInviteFor} />

        <section className="border-b border-border">
          <div className="flex flex-wrap items-center gap-2 px-4 pb-1 pt-3">
            <h2 className="flex-1 text-sm font-semibold text-foreground">Addendum No. 1 ({page.addenda.length})</h2>
            <span className="text-xs text-muted-foreground">
              Invitation email: {page.invitations.sent} sent, {page.invitations.queued} queued, {page.invitations.failed} failed, {page.invitations.not_sent} not sent
            </span>
            {page.is_admin && addendumSessions.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => setInviteFor(addendumSessions[0])}><Mail className="mr-1 h-4 w-4" /> Invitation email</Button>
            )}
          </div>
          <p className="px-4 pb-2 text-xs text-muted-foreground">
            For every active driver whose signed agreement is v5. Nobody is asked to sign until marked present at an Addendum No. 1 session. Declining changes nothing else about the driver's work.
          </p>
          {page.addenda.length === 0
            ? <p className="px-4 pb-4 text-xs text-muted-foreground">No addendum records yet.</p>
            : <ul className="divide-y divide-border">{page.addenda.map((a) => <AddendumItem key={a.id} a={a} canRecord={page.can_record} onChanged={refresh} />)}</ul>}
        </section>

        <section className="border-b border-border">
          <h2 className="px-4 pb-1 pt-3 text-sm font-semibold text-foreground">Drivers on older agreements ({page.older.length})</h2>
          <p className="px-4 pb-2 text-xs text-muted-foreground">Active drivers whose signed agreement is v4 or older, or who have none on file. They are not sent the addendum; they are offered v6 itself, from New contract rollout.</p>
          {page.older.length === 0
            ? <p className="px-4 pb-4 text-xs text-muted-foreground">None.</p>
            : (
              <ul className="divide-y divide-border">
                {page.older.map((o) => (
                  <li key={o.driver_id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
                    <span className="min-w-[12rem] flex-1 text-foreground">{o.name ?? 'Unnamed driver'}</span>
                    <Chip tone="amber">{o.version}</Chip>
                    <span className="text-xs text-muted-foreground">{o.signed_on ? `signed ${formatDay(o.signed_on)}` : 'nothing signed'}</span>
                  </li>
                ))}
              </ul>
            )}
        </section>

        <section className="border-b border-border">
          <h2 className="px-4 pb-1 pt-3 text-sm font-semibold text-foreground">v6 contracts ({page.v6_contracts.length})</h2>
          {page.v6_contracts.length === 0
            ? <p className="px-4 pb-4 text-xs text-muted-foreground">No v6 contract has been issued yet. The next contract sent to a new driver will be v6.</p>
            : (
              <ul className="divide-y divide-border">
                {page.v6_contracts.map((v) => (
                  <li key={v.envelope_id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
                    <span className="min-w-[12rem] flex-1 text-foreground">{v.name ?? 'Unnamed'}</span>
                    <Chip tone="brand">v6</Chip>
                    <span className="text-xs text-muted-foreground">{v.kind}</span>
                    <Chip tone={v.status === 'signed' ? 'green' : v.status === 'sent' ? 'neutral' : 'amber'}>{v.status}</Chip>
                    <span className="text-xs text-muted-foreground">{v.signed_at ? `signed ${formatDay(v.signed_at)}` : `sent ${formatDay(v.sent_at)}`}</span>
                  </li>
                ))}
              </ul>
            )}
        </section>

        <Documents docs={page.documents} />
      </div>

      {newSession && <NewSessionDialog onClose={() => setNewSession(false)} onDone={() => { setNewSession(false); refresh() }} />}
      {inviteFor && (
        <InvitationDialog sessions={addendumSessions} initial={inviteFor} isAdmin={page.is_admin}
          onClose={() => setInviteFor(null)} onDone={() => { setInviteFor(null); refresh() }} />
      )}
    </div>
  )
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="text-xl font-semibold tabular-nums text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

function AddendumItem({ a, canRecord, onChanged }: { a: AddendumRow; canRecord: boolean; onChanged: () => void }) {
  const st = ADDENDUM_STATUS[a.status]
  const openFile = useOpenAgreementFile()
  const resend = useResendAddendum()
  const [dialog, setDialog] = useState<'decline' | 'reopen' | null>(null)
  const [error, setError] = useState<string | null>(null)
  return (
    <li className="px-4 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="min-w-[12rem] flex-1 font-medium text-foreground">{a.driver_name ?? 'Unnamed driver'}</span>
        <Chip tone={st.tone}>{st.label}</Chip>
        {a.invitation_status && <Chip tone={a.invitation_status === 'sent' ? 'green' : a.invitation_status === 'failed' ? 'red' : 'neutral'}>Email {MESSAGE_STATUS_LABEL[a.invitation_status] ?? a.invitation_status}</Chip>}
        <span className="text-xs text-muted-foreground">
          {a.status === 'signed' && a.signed_on ? `Signed ${formatDay(a.signed_on)}`
            : a.status === 'declined' && a.declined_on ? `Declined ${formatDay(a.declined_on)}${a.decline_source === 'signnow' ? ' in SignNow' : ''}`
            : a.status === 'ready_to_sign' ? (a.envelope_sent_at ? `Envelope sent ${formatWhen(a.envelope_sent_at)}` : 'Envelope being prepared')
            : `v5 signed ${formatDay(a.amends_signed_on)}`}
        </span>
        <span className="flex flex-wrap gap-1">
          {a.has_signed_pdf && <Button size="sm" variant="ghost" onClick={() => void openFile({ addendum_id: a.id })}><FileText className="mr-1 h-4 w-4" /> Signed copy</Button>}
          {canRecord && (a.status === 'awaiting_induction' || a.status === 'ready_to_sign') && (
            <Button size="sm" variant="ghost" onClick={() => setDialog('decline')}><UserX className="mr-1 h-4 w-4" /> Record decline</Button>
          )}
          {canRecord && a.status === 'declined' && (
            <Button size="sm" variant="ghost" onClick={() => setDialog('reopen')}><RotateCcw className="mr-1 h-4 w-4" /> Changed their mind</Button>
          )}
          {canRecord && a.status === 'ready_to_sign' && !a.envelope_id && a.envelope_error && (
            <Button size="sm" variant="outline" disabled={resend.loading}
              onClick={() => void resend.trigger({ id: a.id }).then(onChanged).catch((e) => setError(message(e)))}>Send again</Button>
          )}
        </span>
      </div>
      {a.envelope_error && a.status === 'ready_to_sign' && (
        <p className="mt-1 flex items-start gap-1 text-xs text-rose-700 dark:text-rose-300"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {a.envelope_error}</p>
      )}
      {a.decline_note && a.status === 'declined' && <p className="mt-1 text-xs text-muted-foreground">{a.decline_note}</p>}
      {error && <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">{error}</p>}
      {dialog && <DeclineDialog a={a} mode={dialog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged() }} />}
    </li>
  )
}

function DeclineDialog({ a, mode, onClose, onDone }: { a: AddendumRow; mode: 'decline' | 'reopen'; onClose: () => void; onDone: () => void }) {
  const decline = useDeclineAddendum()
  const reopen = useReopenAddendum()
  const [on, setOn] = useState(todaySast())
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const busy = decline.loading || reopen.loading
  const go = async () => {
    setError(null)
    try {
      if (mode === 'decline') await decline.trigger({ id: a.id, declined_on: on, note })
      else await reopen.trigger({ id: a.id, note })
      onDone()
    } catch (e) { setError(message(e)) }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'decline' ? `${a.driver_name} will not sign` : `${a.driver_name} wants to sign after all`}</DialogTitle>
          <DialogDescription>
            {mode === 'decline'
              ? 'Record this only when the driver has said so. Their current agreement continues unchanged, and nothing else about their work changes. It can be reopened if they change their mind.'
              : 'The addendum goes back to awaiting induction, or straight to ready to sign with a new envelope if they already attended a session.'}
          </DialogDescription>
        </DialogHeader>
        {mode === 'decline' && (
          <label className="block space-y-1 text-xs">
            <span className="text-muted-foreground">Date the driver said so</span>
            <Input type="date" value={on} max={todaySast()} onChange={(e) => setOn(e.target.value)} />
          </label>
        )}
        <label className="block space-y-1 text-xs">
          <span className="text-muted-foreground">{mode === 'decline' ? 'How they told you (optional)' : 'When and how they told you'}</span>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </label>
        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Back</Button>
          <Button variant="brand" disabled={busy} onClick={() => void go()}>{busy && <Spinner className="mr-1 h-4 w-4" />}Record</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Sessions({ page, onChanged, onInvite }: { page: AgreementsPage; onChanged: () => void; onInvite: (s: InductionSession) => void }) {
  return (
    <section className="border-b border-border">
      <h2 className="px-4 pb-1 pt-3 text-sm font-semibold text-foreground">Induction sessions ({page.sessions.length})</h2>
      <p className="px-4 pb-2 text-xs text-muted-foreground">
        New-driver sessions use pack 2026-10, emailed to each driver when they are booked. At an Addendum No. 1 session, marking a driver present sends them the addendum to sign.
      </p>
      {page.sessions.length === 0
        ? <p className="px-4 pb-4 text-xs text-muted-foreground">No sessions yet.</p>
        : <ul className="divide-y divide-border">{page.sessions.map((s) => <SessionItem key={s.id} s={s} page={page} onChanged={onChanged} onInvite={onInvite} />)}</ul>}
    </section>
  )
}

function SessionItem({ s, page, onChanged, onInvite }: { s: InductionSession; page: AgreementsPage; onChanged: () => void; onInvite: (s: InductionSession) => void }) {
  const [open, setOpen] = useState(false)
  const [booking, setBooking] = useState(false)
  const mark = useMarkInductionAttendance()
  const [error, setError] = useState<string | null>(null)
  const markOne = async (person: string, status: string) => {
    setError(null)
    let reason: string | null = null
    if (status === 'absent' || status === 'excused') {
      reason = window.prompt('Why? A reason is kept with the record.')
      if (!reason) return
    }
    try { await mark.trigger({ session_id: s.id, person_id: person, status, reason }); onChanged() } catch (e) { setError(message(e)) }
  }
  const present = s.people.filter((p) => p.status === 'attended').length
  return (
    <li>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-left hover:bg-accent/40">
        <span className="min-w-[12rem] flex-1">
          <span className="block text-sm font-medium text-foreground">{SESSION_TYPE_LABEL[s.session_type]}</span>
          <span className="block text-xs text-muted-foreground">{s.starts_at ? formatWhen(s.starts_at) : formatDay(s.session_date)}{s.venue ? `, ${s.venue}` : ''}</span>
        </span>
        <Chip tone="neutral"><Users className="mr-0.5 h-3 w-3" />{s.people.length} booked</Chip>
        <Chip tone={present > 0 ? 'green' : 'neutral'}>{present} present</Chip>
      </button>
      {open && (
        <div className="space-y-2 bg-muted/20 px-4 pb-3">
          {page.can_record && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setBooking(true)}><UserCheck className="mr-1 h-4 w-4" /> Book drivers</Button>
              {s.session_type === 'addendum_1' && page.is_admin && (
                <Button size="sm" variant="outline" onClick={() => onInvite(s)}><Mail className="mr-1 h-4 w-4" /> Invitation email</Button>
              )}
            </div>
          )}
          {s.people.length === 0 ? <p className="text-xs text-muted-foreground">Nobody booked yet.</p> : (
            <ul className="divide-y divide-border rounded-md border border-border bg-background">
              {s.people.map((p) => (
                <li key={p.person_id} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-sm">
                  <span className="min-w-[10rem] flex-1 text-foreground">{p.name ?? 'Unnamed'}</span>
                  <Chip tone={p.status === 'attended' ? 'green' : p.status === 'absent' ? 'red' : 'neutral'}>{ATTENDANCE_LABEL[p.status] ?? p.status}</Chip>
                  {page.can_record && p.status !== 'attended' && (
                    <span className="flex gap-1">
                      <Button size="sm" variant="ghost" disabled={mark.loading} onClick={() => void markOne(p.person_id, 'attended')}>Present</Button>
                      <Button size="sm" variant="ghost" disabled={mark.loading} onClick={() => void markOne(p.person_id, 'absent')}>Absent</Button>
                      <Button size="sm" variant="ghost" disabled={mark.loading} onClick={() => void markOne(p.person_id, 'excused')}>Excused</Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}
        </div>
      )}
      {booking && <BookDialog s={s} onClose={() => setBooking(false)} onDone={() => { setBooking(false); onChanged() }} />}
    </li>
  )
}

function BookDialog({ s, onClose, onDone }: { s: InductionSession; onClose: () => void; onDone: () => void }) {
  const bookable = useGetInductionBookable<Bookable[] | null>()
  const book = useBookInductionSession()
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const loadBookable = bookable.trigger
  useEffect(() => { void loadBookable({ session_id: s.id }, { skipCache: true }).catch(() => undefined) }, [loadBookable, s.id])
  const list = bookable.data ?? []
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const go = async () => {
    setError(null)
    try { await book.trigger({ session_id: s.id, person_ids: Array.from(picked) }); onDone() } catch (e) { setError(message(e)) }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Book drivers onto this session</DialogTitle>
          <DialogDescription>
            {s.session_type === 'new_driver'
              ? 'Each driver booked is emailed the induction pack: the v6 agreement, the Code of Conduct and the presentation.'
              : 'Booking sends nothing and asks nobody to sign. The addendum is sent to sign only when a driver is marked present.'}
          </DialogDescription>
        </DialogHeader>
        {bookable.loading && !bookable.data ? <p className="flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> Loading…</p> : (
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {list.length === 0 && <p className="text-xs text-muted-foreground">Nobody is waiting to be booked onto this session.</p>}
            {list.length > 1 && (
              <button type="button" className="text-xs text-[hsl(var(--brand))] underline" onClick={() => setPicked(new Set(list.map((b) => b.person_id)))}>Choose all {list.length}</button>
            )}
            {list.map((b) => (
              <label key={b.person_id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent/40">
                <input type="checkbox" checked={picked.has(b.person_id)} onChange={() => toggle(b.person_id)} />
                <span className="flex-1 text-foreground">{b.name ?? 'Unnamed'}</span>
                <span className="text-xs text-muted-foreground">{b.stage.replace(/_/g, ' ')}{b.has_email ? '' : ', no email'}</span>
              </label>
            ))}
          </div>
        )}
        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Back</Button>
          <Button variant="brand" disabled={book.loading || picked.size === 0} onClick={() => void go()}>{book.loading && <Spinner className="mr-1 h-4 w-4" />}Book {picked.size || ''}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function NewSessionDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const create = useCreateInductionSession()
  const [type, setType] = useState<'new_driver' | 'addendum_1'>('addendum_1')
  const [date, setDate] = useState('')
  const [start, setStart] = useState('13:00')
  const [end, setEnd] = useState('16:00')
  const [venue, setVenue] = useState('155 West Street, Sandown, Sandton')
  const [capacity, setCapacity] = useState('')
  const [error, setError] = useState<string | null>(null)
  const go = async () => {
    setError(null)
    const startsAt = sastInstant(date, start)
    if (!startsAt) { setError('Give the date and the start time.'); return }
    try {
      await create.trigger({ session_type: type, starts_at: startsAt, ends_at: sastInstant(date, end), venue, capacity })
      onDone()
    } catch (e) { setError(message(e)) }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New induction session</DialogTitle>
          <DialogDescription>Times are South African time. The venue is written exactly as drivers will read it in the email.</DialogDescription>
        </DialogHeader>
        <label className="block space-y-1 text-xs">
          <span className="text-muted-foreground">Type</span>
          <NativeSelect value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="addendum_1">Addendum No. 1 session (drivers on v5)</option>
            <option value="new_driver">New-driver induction (pack 2026-10)</option>
          </NativeSelect>
        </label>
        <div className="grid grid-cols-3 gap-2">
          <label className="col-span-3 block space-y-1 text-xs sm:col-span-1"><span className="text-muted-foreground">Date</span><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <label className="block space-y-1 text-xs"><span className="text-muted-foreground">Starts</span><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="block space-y-1 text-xs"><span className="text-muted-foreground">Ends</span><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
        <label className="block space-y-1 text-xs"><span className="text-muted-foreground">Venue</span><Input value={venue} onChange={(e) => setVenue(e.target.value)} /></label>
        <label className="block space-y-1 text-xs"><span className="text-muted-foreground">Places (optional)</span><Input inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} /></label>
        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Back</Button>
          <Button variant="brand" disabled={create.loading} onClick={() => void go()}>{create.loading && <Spinner className="mr-1 h-4 w-4" />}Create session</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function InvitationDialog({ sessions, initial, isAdmin, onClose, onDone }: {
  sessions: InductionSession[]; initial: InductionSession; isAdmin: boolean; onClose: () => void; onDone: () => void
}) {
  const [sessionId, setSessionId] = useState(initial.id)
  const preview = useGetAddendumInvitationPreview<InvitationPreview | null>()
  const send = useSendAddendumInvitations<{ queued: number }>()
  const [confirm, setConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadPreview = preview.trigger
  useEffect(() => { void loadPreview({ session_id: sessionId, samples: 2 }, { skipCache: true }).catch(() => undefined) }, [loadPreview, sessionId])
  const p = preview.data
  const go = async () => {
    setError(null)
    try { await send.trigger({ session_id: sessionId }); onDone() } catch (e) { setError(message(e)) }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Addendum No. 1 invitation email</DialogTitle>
          <DialogDescription>Exactly what each driver receives, rendered by the same function that sends it. Nothing is sent until you press Send.</DialogDescription>
        </DialogHeader>
        {sessions.length > 1 && (
          <NativeSelect value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
            {sessions.map((s) => <option key={s.id} value={s.id}>{s.starts_at ? formatWhen(s.starts_at) : formatDay(s.session_date)}, {s.venue}</option>)}
          </NativeSelect>
        )}
        {preview.loading && !p ? <p className="flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> Rendering…</p>
          : preview.error ? <p className="text-xs text-rose-700">{preview.error}</p>
          : p && p.ok ? (
            <div className="space-y-3 text-xs">
              <p className="text-foreground">Attached to every email: {p.attachments.map((a) => `${a.filename} (${kb(a.bytes)})`).join(' and ')}.</p>
              {p.samples.map((s) => (
                <div key={s.to} className="space-y-1 rounded-md border border-border p-3">
                  <div><span className="text-muted-foreground">To </span><span className="font-medium text-foreground">{s.driver_name}, {s.to}</span></div>
                  <div><span className="text-muted-foreground">Subject </span><span className="text-foreground">{s.subject}</span></div>
                  <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-relaxed text-foreground">{s.body_text}</pre>
                </div>
              ))}
              <div>
                <p className="mb-1 font-medium text-foreground">Recipients: {p.will_send} will be sent</p>
                <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
                  {p.recipients.map((r) => (
                    <li key={r.addendum_id} className="flex flex-wrap gap-2 px-2 py-1">
                      <span className="flex-1 text-foreground">{r.driver_name}</span>
                      <span className="text-muted-foreground">{r.email ?? 'no email'}</span>
                      {!r.sendable && <span className="text-amber-700 dark:text-amber-300">{r.reason}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : <p className="text-xs text-rose-700">{p?.reason ?? 'The preview could not be made.'}</p>}
        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isAdmin && p?.ok && p.will_send > 0 && (
            <label className="mr-auto flex items-center gap-2 text-xs text-foreground">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              I have read the preview and want {p.will_send} drivers emailed now
            </label>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {isAdmin && (
            <Button variant="brand" disabled={!confirm || send.loading || !p?.ok || p.will_send === 0} onClick={() => void go()}>
              {send.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Mail className="mr-1 h-4 w-4" />}Send to {p?.will_send ?? 0}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Documents({ docs }: { docs: AgreementsPage['documents'] }) {
  const openFile = useOpenAgreementFile()
  return (
    <section>
      <h2 className="px-4 pb-1 pt-3 text-sm font-semibold text-foreground">Documents on file ({docs.length})</h2>
      <p className="px-4 pb-2 text-xs text-muted-foreground">Every file a driver is sent or signs, with its sha256, so a signed copy can be matched to the exact text.</p>
      <ul className="divide-y divide-border pb-4">
        {docs.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
            <span className="min-w-[14rem] flex-1">
              <span className="block text-foreground">{d.title}</span>
              <span className="block text-xs text-muted-foreground">{DOCUMENT_SET_LABEL[d.document_set] ?? d.document_set}, {kb(d.bytes)}, sha256 <span className="font-mono">{shortHash(d.sha256)}</span></span>
            </span>
            <Button size="sm" variant="ghost" onClick={() => void openFile({ document_id: d.id })}><FileText className="mr-1 h-4 w-4" /> Open</Button>
          </li>
        ))}
      </ul>
    </section>
  )
}
